import React, { useContext } from 'react';
import Head from 'next/head';
import { ServerInfoContext } from '../../contexts';
import { buildSafeCanonicalUrl } from '../../utils/build-safe-canonical-url';
import { notEmpty } from '../../utils';
import { isNoAgentPhoto } from '../shared/avatar';

import {
  DigitalDataInit,
  DigitalDataTag,
  GoogleSchema,
  ImageResourceHints,
  MetaTags,
  PaginationSeoLinks,
} from './components';
import {
  createBreadcrumbsJSONBlob,
  generateDigitalDataSearchResults,
} from './utils';
import { ServerInfo, UserDetailsRedacted } from '../../@types';
import { BreadCrumbs } from './utils/create-breadcrumbs-blob';
import { buildUrl } from '../search-results/utils';
import type {
  AgencySearchQuery,
  ContactSearchProfileCardFragment,
  AgencySearchProfileCardFragment,
} from '../../generated/graphql';
import { makeCollectionPageSchema } from '../head-profile/components/head-snippets/schema-generators';

export type SearchResultsHeadProps = {
  isAgency: boolean;
  total?: number | null;
  totalPages?: number | null;
  suburbOptions: AgencySearchQuery['locationProfiles'];
  suburbSlugsArray: string[];
  sortBy: string;
  agentType: string;
  keyword?: string;
  breadcrumbs: BreadCrumbs;
  pageNumber: number;
  agencyIds?: number[];
  user?: UserDetailsRedacted;
  agentResults?: Array<ContactSearchProfileCardFragment | null> | null;
  agencyResults?: Array<AgencySearchProfileCardFragment | null> | null;
};

const realPhoto = (url?: string | null): string | undefined =>
  url && !isNoAgentPhoto(url) ? url : undefined;

const SearchResultsHead = ({
  isAgency,
  total,
  suburbOptions,
  suburbSlugsArray,
  sortBy,
  agentType,
  keyword,
  breadcrumbs,
  pageNumber,
  totalPages,
  agencyIds,
  user,
  agentResults,
  agencyResults,
}: SearchResultsHeadProps): JSX.Element => {
  const suburbsText = suburbOptions
    ?.map(
      (suburb) => `${suburb?.suburbName} ${suburb?.state} ${suburb?.postcode}`,
    )
    .join(', ');
  const title = `${total} Real Estate ${
    isAgency ? 'Agencies' : 'Agents'
  } active in ${suburbsText}`;
  const agencyCount = typeof total === 'number' && total > 0 ? `${total} ` : '';
  const description = isAgency
    ? `Looking for the best real estate agency in ${suburbsText}? Browse ${agencyCount}local real estate agencies with reviews and recent results, all in one place.`
    : `Selling in ${suburbsText}? Compare top real estate agents by recent sales, local experience and reviews to find the right agent for your property.`;

  const {
    sysEnv,
    baseUrl,
    path,
    source,
    isEmbeddedApp,
    trackingDisabled,
    noIndexFollow,
  } = useContext<ServerInfo>(ServerInfoContext);

  // canonical url
  // multiple suburbs or any filter is considered a user action and not SEOable.
  const shouldIndex = suburbOptions?.length === 1 && !keyword;
  const canonical = buildSafeCanonicalUrl(baseUrl, path);

  const digitalData = generateDigitalDataSearchResults({
    isAgency,
    sysEnv,
    source,
    isEmbeddedApp,
    pageNumber,
    agencyIds,
    suburbs: suburbOptions,
    user,
  });

  const firstAgency = agencyResults?.find(notEmpty);
  const firstAgent = agentResults?.find(notEmpty);

  const firstLogoUrl = isAgency
    ? firstAgency?.logoSmall
    : firstAgent?.agencyLogoUrl;

  const firstPhotoUrl = isAgency
    ? firstAgency?.agents?.map((agent) => agent?.profilePhoto).find(realPhoto)
    : firstAgent?.profilePhoto;

  const lcpImageUrl = isAgency
    ? firstLogoUrl
    : (realPhoto(firstPhotoUrl) ?? firstLogoUrl);

  // Generate collection page schema
  const collectionPageSchema = makeCollectionPageSchema({
    baseUrl,
    currentUrl: path,
    isAgency,
    agentResults,
    agencyResults,
    locationProfiles:
      suburbOptions?.map((profile) => ({
        suburbName: profile?.suburbName,
        state: profile?.state,
        postcode: profile?.postcode,
      })) || null,
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        {shouldIndex && <link rel="canonical" href={canonical} />}
      </Head>
      <ImageResourceHints lcpImageUrl={lcpImageUrl} />
      <MetaTags
        noIndexFollow={!!noIndexFollow && !shouldIndex}
        description={description}
      />
      <GoogleSchema
        breadcrumbsJSON={createBreadcrumbsJSONBlob(breadcrumbs)}
        collectionPageSchema={collectionPageSchema || undefined}
      />

      <DigitalDataTag digitalData={digitalData} />
      <DigitalDataInit trackingDisabled={trackingDisabled} />

      <PaginationSeoLinks
        seoPaginationPrev={
          pageNumber === 1
            ? undefined
            : buildUrl({
                isAgency,
                suburbs: suburbSlugsArray,
                sortBy,
                agentFilter: agentType,
                keywordName: keyword,
                page: pageNumber - 1,
              }).as
        }
        seoPaginationNext={
          pageNumber === totalPages
            ? undefined
            : buildUrl({
                isAgency,
                suburbs: suburbSlugsArray,
                sortBy,
                agentFilter: agentType,
                keywordName: keyword,
                page: pageNumber + 1,
              }).as
        }
      />
    </>
  );
};

export default SearchResultsHead;
